// src/app/(auth)/reset-password/page.tsx
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Suspense, useMemo, useState, type FormEvent } from 'react';
import * as authService from '@/lib/services/auth';
import { PasswordField } from '@/components/ui/PasswordField';

type Rule = {
  id: string;
  label: string;
  test: (pw: string) => boolean;
};

const RULES: Rule[] = [
  { id: 'len', label: '12 characters minimum', test: (p) => p.length >= 12 },
  { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'num', label: 'One number', test: (p) => /\d/.test(p) },
  { id: 'sym', label: 'One symbol (!@#$%…)', test: (p) => /[^\w\s]/.test(p) },
];

function ResetPasswordPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // From the code step (router state), or an older emailed link (?token=).
  const stateToken = (location.state as { token?: string } | null)?.token;
  const token = stateToken ?? searchParams.get('token') ?? '';

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rulePass = useMemo(
    () =>
      Object.fromEntries(
        RULES.map((r) => [r.id, r.test(pw)]),
      ) as Record<string, boolean>,
    [pw],
  );

  const allMet = RULES.every((r) => rulePass[r.id]);
  const match = pw.length > 0 && pw === confirm;
  const canSubmit = allMet && match;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();

    if (!canSubmit || !token) return;

    setError(null);
    setIsLoading(true);

    try {
      await authService.confirmPasswordReset(token, pw, confirm);
      navigate('/login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main
      className="ambient-bg min-h-screen flex items-center justify-center px-6"
    >
      <form
        onSubmit={onSubmit}
        className="screen-fade"
        style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 2 }}
      >
        <header className="mb-10">
          <span className="mono-label">Account Recovery</span>

          <h1
            className="font-serif mt-2"
            style={{
              fontSize: 36,
              color: 'var(--text-primary)',
              letterSpacing: '-0.005em',
            }}
          >
            Set a new credential
          </h1>
        </header>

        {!token && (
          <p className="mono-label" role="alert" style={{ color: 'var(--color-error)', marginBottom: 20 }}>
            This page needs a verified code. <Link to="/forgot-password" style={{ textDecoration: 'underline' }}>Start again</Link>
          </p>
        )}

        <div className="space-y-6">
          {/* PASSWORD */}
          <PasswordField
            label="New password"
            value={pw}
            onChange={setPw}
            autoComplete="new-password"
            className="underline-input"
          />

          {/* CONFIRM PASSWORD */}
          <PasswordField
            label="Confirm"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            className="underline-input"
          />
        </div>

        {/* PASSWORD RULES */}
        <ul
          className="mt-7 space-y-2"
          style={{ listStyle: 'none', padding: 0 }}
        >
          {RULES.map((r) => {
            const ok = rulePass[r.id];

            return (
              <li key={r.id} className="flex items-center gap-2.5">
                <span
                  className={`led ${ok ? 'led-green' : 'led-red'}`}
                  aria-hidden
                />

                <span
                  style={{
                    color: ok
                      ? 'rgba(var(--fg-rgb),0.75)'
                      : 'rgba(var(--fg-rgb),0.4)',
                    fontSize: 12.5,
                  }}
                >
                  {r.label}
                </span>
              </li>
            );
          })}

          <li className="flex items-center gap-2.5 pt-1">
            <span
              className={`led ${match ? 'led-green' : 'led-red'}`}
              aria-hidden
            />

            <span
              style={{
                color: match
                  ? 'rgba(var(--fg-rgb),0.75)'
                  : 'rgba(var(--fg-rgb),0.4)',
                fontSize: 12.5,
              }}
            >
              Confirmation matches
            </span>
          </li>
        </ul>

        <button
          type="submit"
          disabled={!canSubmit || isLoading}
          className="btn-pill"
          style={{
            width: '100%',
            marginTop: 32,
            opacity: canSubmit && !isLoading ? 1 : 0.4,
            cursor:
              canSubmit && !isLoading ? 'pointer' : 'not-allowed',
          }}
        >
          {isLoading ? 'Updating…' : 'Update Credential'}
        </button>

        {error && (
          <p
            className="mono-label"
            style={{
              marginTop: 10,
              color: 'var(--color-error)',
              textAlign: 'center',
            }}
          >
            {error}
          </p>
        )}

        <div className="flex items-center justify-center mt-5">
          <Link
            to="/login"
            className="mono-label"
            style={{ color: 'rgba(var(--fg-rgb),0.45)' }}
          >
            ← Return to login
          </Link>
        </div>
      </form>
    </main>
  );
}

export default function ResetPasswordPage() {
  return (
    <Suspense>
      <ResetPasswordPageContent />
    </Suspense>
  );
}